import React, { FC } from "react";
import { Trans } from "react-i18next";
import compact from "lodash/compact";

import Table, { TableBody, TableHeader } from "../../../../components/Table";
import { TableProps } from "./types";
import Icon from "../../../../components/Icon";
import { IconName } from "../../../../components/Icon/types";
import { Direction } from "../../../../components/Table/components/TableHeader/types";
import { getProviderName } from "../../../../utils/table";
import { ACTION, SUBJECT } from "../../../../constants/permissions";
import { useAbility } from "../../../../hooks";
import { SUBSCRIPTION_LABEL } from "../../../../constants/spCode";
import { getMonthDayYearDate } from "../../../../utils/common";
import {
  ITableColumnConfig,
  ITableDataParams,
} from "../../../../components/Table/types";
import { Offering } from "../../../../services/api/offering/types";

import "./styles.scss";

export const OfferingsTable: FC<TableProps> = ({
  data,
  defaultSort,
  onSort,
  onEdit,
  renderExpandedRow,
}) => {
  const ability = useAbility();
  const columns: ITableColumnConfig[] = compact([
    {
      field: "sp_code",
      headerName: "Sub. Provider",
      cellClass: "ams-offerings-table__cell",
      cellRenderer: ({ rowData }: ITableDataParams<Offering>) => {
        if (!rowData) {
          return <div />;
        }
        const providerName = getProviderName(rowData.sp_code) as IconName;

        return (
          <div className="ams-table__cell-content--line">
            <Icon className="ams-tenant-icon">{providerName}</Icon>
            <span>
              <Trans>{providerName}</Trans>
            </span>
          </div>
        );
      },
    },
    {
      field: "name",
      headerName: "Offer Name",
      cellClass: "ams-offerings-table__cell",
      cellRenderer: ({ rowData }: ITableDataParams<Offering>) => {
        if (!rowData) {
          return <div />;
        }
        const icon = SUBSCRIPTION_LABEL[rowData.product_line_code]
          .label as IconName;

        return (
          <div className="ams-table__cell-content--line ams-table__cell-content--text">
            <Icon className="ams-tenant-icon">{icon}</Icon>
            <span
              className="ams-table__cell-content--text-block"
              title={rowData.name}
            >
              {rowData.name}
            </span>
          </div>
        );
      },
    },
    {
      field: "ends_at",
      headerName: "End Date",
      cellClass: "ams-offerings-table__cell",
      defaultSort: "desc" as Direction,
      valueGetter: ({ rowData }: ITableDataParams<Offering>) => {
        if (!rowData) {
          return "";
        }
        if (!rowData.ends_at && isNaN(Date.parse(rowData.ends_at))) {
          return "- - -";
        }

        return getMonthDayYearDate(new Date(rowData.ends_at));
      },
    },
    {
      field: "product_line__default_offering_code",
      headerName: "Default",
      cellClass: "ams-offerings-table__cell",
      cellRenderer: ({ rowData }: ITableDataParams<Offering>) => {
        const icon: IconName = "default";

        if (!rowData || !rowData.isDefault) {
          return <div />;
        }

        return <Icon>{icon}</Icon>;
      },
    },
    {
      field: "updated_at",
      headerName: "Last Updated",
      cellClass: "ams-offerings-table__cell",
      defaultSort: "desc" as Direction,
      valueGetter: ({ rowData }: ITableDataParams<Offering>) => {
        if (!rowData) {
          return "";
        }
        if (!rowData.updated_at && isNaN(Date.parse(rowData.updated_at))) {
          return "";
        }

        const updatedAtDate = new Date(rowData.updated_at);
        const [, monthName, date, year] = updatedAtDate
          .toDateString()
          .split(" ");

        return `${monthName} ${date}, ${year}`;
      },
    },
    ability.can(ACTION.WRITE, SUBJECT.PRODUCT)
      ? {
          field: "edit",
          headerName: "",
          cellClass: "ams-offerings-table__cell",
          sortable: false,
          cellRenderer: ({ rowData }: ITableDataParams<Offering>) => {
            const icon: IconName = "edit";

            return (
              <div
                onClick={() => {
                  onEdit(rowData);
                }}
              >
                <Icon>{icon}</Icon>
              </div>
            );
          },
        }
      : null,
  ]);

  return (
    <Table className="ams-offerings-table">
      <TableHeader
        columns={columns}
        defaultSort={defaultSort}
        onSort={onSort}
        isExpanded={!!renderExpandedRow}
      />
      <TableBody
        columns={columns}
        data={data}
        renderExpandedRow={renderExpandedRow}
      />
    </Table>
  );
};
